import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, ShieldAlert, ScrollText, LogOut, Zap, User, Activity
} from "lucide-react";
import axios from "axios";
import KillSwitchModal from "./KillSwitchModal";
import RiskPulseBar from "./RiskPulseBar";
import { API } from "../../contexts/AuthContext";

const NAV_ITEMS = [
  { to: "/", icon: LayoutDashboard, label: "Command Wall" },
  { to: "/intelligence", icon: Activity, label: "Intelligence" },
  { to: "/risk", icon: ShieldAlert, label: "Risk" },
  { to: "/logs", icon: ScrollText, label: "Logs" },
];

// ─── Sparkline (SVG) ─────────────────────────────────────────────
function Sparkline({ data, width = 80, height = 24 }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.equity);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");

  const isPositive = values[values.length - 1] >= values[0];
  const color = isPositive ? "#22C55E" : "#EF4444";

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block" data-testid="equity-sparkline">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End dot */}
      <circle
        cx={(values.length - 1) / (values.length - 1) * width}
        cy={height - ((values[values.length - 1] - min) / range) * (height - 4) - 2}
        r="2"
        fill={color}
      />
    </svg>
  );
}

function MetricBlock({ label, value, color, testid }) {
  return (
    <div className="metric-capsule-wrap">
      <div className="metric-capsule-inner">
        <div style={{
          fontSize: "9px",
          letterSpacing: "0.16em",
          color: "#475569",
          fontFamily: "'Inter Tight', sans-serif",
          fontWeight: "500",
          textTransform: "uppercase",
          marginBottom: "2px",
        }}>
          {label}
        </div>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "600",
          fontSize: "13px",
          color,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }} data-testid={testid}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children, summary }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showKill, setShowKill] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [equityHistory, setEquityHistory] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/equity-history`);
        setEquityHistory(data);
      } catch (e) { /* silent */ }
    };
    fetchHistory();
    const iv = setInterval(fetchHistory, 60000);
    return () => clearInterval(iv);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const pnl = summary?.total_pnl || 0;
  const pnlColor = pnl >= 0 ? "#22C55E" : "#EF4444";
  const pnlStr = `${pnl >= 0 ? "+" : ""}${pnl.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  const equityStr = `$${(summary?.total_equity || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  const marginStr = `${(summary?.margin_usage || 0).toFixed(1)}%`;
  const platformsStr = `${summary?.active_platforms || 0} / ${summary?.connected_platforms || 0}`;

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "#0B0D10", position: "relative" }}>

      {/* Background layers */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute inset-0 ace-bg-radial" />
        <div className="ace-noise" />
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen z-50 flex flex-col border-r transition-all duration-300 ${sidebarExpanded ? "w-52" : "w-[60px]"}`}
        style={{ borderColor: "rgba(255,255,255,0.05)", background: "rgba(11,13,16,0.97)", backdropFilter: "blur(20px)" }}
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center h-[52px] px-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          <div className="flex-shrink-0 w-8 h-8 octa-clip-sm bg-[#2563EB] flex items-center justify-center">
            <Zap size={14} className="text-white" strokeWidth={2.5} />
          </div>
          {sidebarExpanded && (
            <div className="ml-3 overflow-hidden">
              <div className="font-heading font-bold text-white text-[11px] tracking-[0.2em] uppercase">ACE OS</div>
              <div style={{ fontSize: "8px", letterSpacing: "0.12em", color: "#475569", fontFamily: "'Inter Tight', sans-serif", fontWeight: "500" }}>COMMAND</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/ /g, '-')}`}
              className={({ isActive }) =>
                `flex items-center h-9 px-2 transition-all duration-150 ${
                  isActive ? "text-[#2563EB]" : "text-[#475569] hover:text-[#94A3B8]"
                }`
              }
              style={({ isActive }) => isActive ? {
                background: "rgba(37,99,235,0.08)",
                clipPath: "polygon(5px 0%, calc(100% - 5px) 0%, 100% 5px, 100% calc(100% - 5px), calc(100% - 5px) 100%, 5px 100%, 0% calc(100% - 5px), 0% 5px)",
              } : {}}
            >
              <Icon size={16} strokeWidth={1.5} className="flex-shrink-0" />
              {sidebarExpanded && (
                <span className="ml-3 text-xs font-medium whitespace-nowrap" style={{ fontFamily: "'Inter Tight', sans-serif" }}>{label}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="px-2 pb-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.03)" }}>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center w-full h-9 px-2 text-[#475569] hover:text-[#EF4444] transition-all duration-150"
          >
            <LogOut size={15} strokeWidth={1.5} />
            {sidebarExpanded && <span className="ml-3 text-xs" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "500" }}>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col ml-[60px] overflow-hidden" style={{ position: "relative", zIndex: 1 }}>

        {/* Risk Pulse Bar — always visible */}
        <RiskPulseBar />

        {/* Top command bar */}
        <header
          className="flex items-center justify-between px-6 flex-shrink-0"
          style={{
            height: "52px",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            background: "rgba(11,13,16,0.96)",
            backdropFilter: "blur(20px)",
            zIndex: 40,
          }}
          data-testid="topbar"
        >
          {/* Metric capsules + sparkline */}
          <div className="flex items-center gap-2">
            <MetricBlock label="Equity" value={equityStr} color="#E2E8F0" testid="topbar-equity" />

            {/* Sparkline */}
            {equityHistory && (
              <div className="flex items-center px-2">
                <Sparkline data={equityHistory} width={72} height={22} />
              </div>
            )}

            <div style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.04)" }} />
            <MetricBlock label="Unrealized PnL" value={pnlStr} color={pnlColor} testid="topbar-pnl" />
            <div style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.04)" }} />
            <MetricBlock label="Margin" value={marginStr} color="#38BDF8" testid="topbar-margin" />
            <div style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.04)" }} />
            <MetricBlock label="Platforms" value={platformsStr} color="#E2E8F0" testid="topbar-platforms" />
            <div style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.04)" }} />
            {/* System status */}
            <div className="metric-capsule-wrap">
              <div className="metric-capsule-inner flex items-center gap-2">
                <span className="live-dot" style={{ display: "inline-block", width: "5px", height: "5px", borderRadius: "50%", background: "#22C55E", flexShrink: 0 }} />
                <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: "#22C55E", letterSpacing: "0.12em", fontWeight: "600" }}>LIVE</span>
              </div>
            </div>
          </div>

          {/* Kill switch + user */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowKill(true)}
              data-testid="kill-switch-btn"
              className="transition-all duration-150 hover:brightness-110"
              style={{
                clipPath: "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)",
                background: "rgba(239,68,68,0.06)",
                border: "1px solid rgba(239,68,68,0.15)",
                color: "#EF4444",
                padding: "5px 14px",
                fontFamily: "'Inter Tight', sans-serif",
                fontWeight: "600",
                fontSize: "9px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              KILL SWITCH
            </button>
            <div className="flex items-center gap-2 text-[#475569]">
              <div className="w-7 h-7 octa-clip-sm flex items-center justify-center" style={{ background: "rgba(255,255,255,0.04)" }}>
                <User size={12} />
              </div>
              {user && (
                <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", color: "#475569", fontWeight: "500" }} className="hidden md:block">
                  {user.name || user.email}
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
          {/* Footer */}
          <div className="py-4 text-center" style={{ borderTop: "1px solid rgba(255,255,255,0.03)", marginTop: "auto" }} data-testid="app-footer">
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "9px", color: "#334155" }}>
              ACE OS — Operated by <a href="https://www.gftworld.in" target="_blank" rel="noopener noreferrer" className="hover:text-[#94A3B8] transition-colors" style={{ color: "#475569", textDecoration: "underline", textUnderlineOffset: "2px" }}>www.gftworld.in</a>
            </span>
          </div>
        </main>
      </div>

      {showKill && <KillSwitchModal onClose={() => setShowKill(false)} />}
    </div>
  );
}
